import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { inject, Injectable, InjectionToken, NgZone, PLATFORM_ID, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

import { IDLE_EXCLUDED_PATHS, IDLE_TIMEOUT_CONFIG } from '../../constants/idle-timeout.constants';
import { AppStateResetService } from './app-state-reset.service';
import { LogoutService } from './logout.service';
import { UserService } from './user.service';

/** Message exchanged between tabs of the same browser. */
interface IdleChannelMessage {
  readonly type: 'activity' | 'logout';
  /** `customerId:userId` the message belongs to — another user's (or tenant's) tab must ignore it. */
  readonly uid: string;
}

/** The slice of `BroadcastChannel` this service uses — lets specs supply a fake. */
export interface IdleChannel {
  onmessage: ((event: { data: IdleChannelMessage }) => void) | null;
  postMessage(message: IdleChannelMessage): void;
  close(): void;
}

/** Creates the cross-tab channel, or `null` where `BroadcastChannel` is unavailable. */
export const IDLE_CHANNEL_FACTORY = new InjectionToken<() => IdleChannel | null>('IDLE_CHANNEL_FACTORY', {
  providedIn: 'root',
  factory: () => () =>
    typeof BroadcastChannel === 'undefined' ? null : (new BroadcastChannel('regtrack-license-idle') as unknown as IdleChannel),
});

/** Events that count as the user being present. `mousemove` is deliberately absent. */
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel'] as const;

/**
 * Ends the session after a period of inactivity (VAPT #8) — warns first, then calls
 * {@link LogoutService.logout}. Browser-only; started once from `AppComponent`.
 *
 * Everything that fires often (activity listeners, the countdown timer, the cross-tab
 * channel) runs OUTSIDE the Angular zone so it neither blocks hydration cleanup nor
 * triggers change detection per keystroke; only the warning toggle and the logout
 * re-enter the zone. Deadlines are re-checked against `Date.now()` whenever a timer
 * fires, the tab becomes visible, or a page is restored, so a slept laptop logs out on
 * return instead of waiting for a throttled timer.
 */
@Injectable({ providedIn: 'root' })
export class IdleTimeoutService {
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);
  private readonly doc = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly userService = inject(UserService);
  private readonly logoutService = inject(LogoutService);
  private readonly appReset = inject(AppStateResetService);
  private readonly config = inject(IDLE_TIMEOUT_CONFIG);
  private readonly channelFactory = inject(IDLE_CHANNEL_FACTORY);

  /** True while the "you are about to be signed out" dialog should show. */
  readonly warningVisible = signal(false);
  /** Milliseconds left before logout while the warning is visible. */
  readonly remainingMs = signal(0);

  private initialised = false;
  private running = false;
  private loggingOut = false;
  private lastActivity = 0;
  private lastBroadcast = 0;
  private timerId: ReturnType<typeof setTimeout> | null = null;
  private channel: IdleChannel | null = null;

  private readonly onActivity = (): void => {
    if (this.warningVisible()) return; // only "Stay signed in" extends an open warning
    this.lastActivity = Date.now();
    this.broadcastActivity(false);
  };
  private readonly onWake = (): void => {
    if (this.doc.visibilityState === 'hidden') return;
    this.check();
  };

  /** Idempotent. Call once from the browser after first render. */
  init(): void {
    if (!isPlatformBrowser(this.platformId) || this.initialised) return;
    this.initialised = true;
    this.appReset.register(() => this.stop());
    this.zone.runOutsideAngular(() => {
      this.router.events
        .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
        .subscribe(() => this.evaluate());
    });
    this.evaluate();
  }

  /** "Stay signed in" — counts as activity and tells the other tabs. */
  extend(): void {
    if (!this.running) return;
    // A throttled background timer can leave the warning open past the deadline; a late click must
    // not revive a session that has already expired.
    if (Date.now() - this.lastActivity >= this.config.timeoutMs) {
      this.expire();
      return;
    }
    this.lastActivity = Date.now();
    this.zone.run(() => this.warningVisible.set(false));
    this.broadcastActivity(true);
    this.schedule();
  }

  /** The dialog's Logout button. */
  logoutNow(): void {
    this.expire();
  }

  private evaluate(): void {
    const path = this.router.url.split(/[?#]/)[0];
    const shouldRun = this.userService.isSessionVerified() && !IDLE_EXCLUDED_PATHS.includes(path);
    if (shouldRun && !this.running) this.start();
    else if (!shouldRun && this.running) this.stop();
  }

  private start(): void {
    this.running = true;
    this.loggingOut = false;
    this.lastActivity = Date.now();
    // Not carried over from a previous session, or the new user's first activity is throttled away.
    this.lastBroadcast = 0;
    this.zone.runOutsideAngular(() => {
      for (const name of ACTIVITY_EVENTS) {
        this.doc.addEventListener(name, this.onActivity, { capture: true, passive: true });
      }
      this.doc.addEventListener('visibilitychange', this.onWake);
      this.doc.defaultView?.addEventListener('pageshow', this.onWake);
      this.channel = this.channelFactory();
      if (this.channel) this.channel.onmessage = (event) => this.onRemote(event.data);
    });
    this.schedule();
  }

  private stop(): void {
    if (!this.running) return;
    this.running = false;
    for (const name of ACTIVITY_EVENTS) {
      this.doc.removeEventListener(name, this.onActivity, { capture: true });
    }
    this.doc.removeEventListener('visibilitychange', this.onWake);
    this.doc.defaultView?.removeEventListener('pageshow', this.onWake);
    this.clearTimer();
    this.channel?.close();
    this.channel = null;
    this.zone.run(() => this.warningVisible.set(false));
  }

  private clearTimer(): void {
    if (this.timerId !== null) clearTimeout(this.timerId);
    this.timerId = null;
  }

  /** Arms one timer for the next checkpoint (warning, then logout, then the 1s countdown). */
  private schedule(): void {
    this.clearTimer();
    if (!this.running) return;
    const idle = Date.now() - this.lastActivity;
    const warnAt = this.config.timeoutMs - this.config.warningMs;
    const delay = this.warningVisible() ? 1000 : Math.max(warnAt - idle, 0);
    this.zone.runOutsideAngular(() => {
      this.timerId = setTimeout(() => this.check(), delay);
    });
  }

  private check(): void {
    if (!this.running) return;
    const idle = Date.now() - this.lastActivity;
    if (idle >= this.config.timeoutMs) {
      this.expire();
      return;
    }
    const warnAt = this.config.timeoutMs - this.config.warningMs;
    if (idle >= warnAt) {
      this.remainingMs.set(this.config.timeoutMs - idle);
      if (!this.warningVisible()) this.zone.run(() => this.warningVisible.set(true));
    } else if (this.warningVisible()) {
      this.zone.run(() => this.warningVisible.set(false));
    }
    this.schedule();
  }

  private expire(): void {
    if (this.loggingOut) return;
    this.loggingOut = true;
    this.channel?.postMessage({ type: 'logout', uid: this.sessionKey() });
    this.endSession();
  }

  private endSession(): void {
    this.stop();
    this.zone.run(() => this.logoutService.logout());
  }

  private onRemote(msg: IdleChannelMessage): void {
    if (!this.running || msg.uid !== this.sessionKey()) return;
    if (msg.type === 'logout') {
      if (this.loggingOut) return;
      this.loggingOut = true;
      this.endSession();
      return;
    }
    this.lastActivity = Date.now();
    if (this.warningVisible()) this.zone.run(() => this.warningVisible.set(false));
    this.schedule();
  }

  /** Identifies the signed-in user across tabs; the numeric user id alone can repeat across customers. */
  private sessionKey(): string {
    return `${this.userService.getCustomerId()}:${this.userService.getUserId()}`;
  }

  private broadcastActivity(force: boolean): void {
    const now = Date.now();
    if (!force && now - this.lastBroadcast < this.config.broadcastThrottleMs) return;
    this.lastBroadcast = now;
    this.channel?.postMessage({ type: 'activity', uid: this.sessionKey() });
  }
}
